import { useEffect, useState } from 'react'
import { ApiError, getForecast, type WeatherForecast } from './api/weather'
import { WeatherList } from './components/WeatherList'
import { LocationManager } from './components/LocationManager'
import { ContactPanel } from './components/ContactPanel'
import { Login } from './components/Login'
import { logout } from './auth'

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: WeatherForecast[] }

export type Unit = 'C' | 'F'

// The panels behind the tab strip: the read-only forecast, CRUD over locations, and a
// contacts panel that has nothing to do with weather -- it demonstrates Ebi.Common.Bol.
type Tab = 'forecast' | 'locations' | 'contacts'

// Auth state comes from the SERVER, and there is no endpoint that just reports it:
// the cookie is HttpOnly, so the only way to know is to request something that
// needs auth. We use the forecast, which the app wants regardless — so the check
// costs no extra round trip.
type Auth = 'checking' | 'in' | 'out'

export default function App() {
  const [auth, setAuth] = useState<Auth>('checking')
  const [state, setState] = useState<State>({ status: 'loading' })

  async function load() {
    setState({ status: 'loading' })
    try {
      const data = await getForecast(7)
      setAuth('in')
      setState({ status: 'ready', data })
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAuth('out')
        return
      }
      // Any other failure leaves us assuming we're signed in and showing the error.
      // Bouncing to the login screen on a 500 would be misleading, and signing in
      // again wouldn't fix it.
      setAuth('in')
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  useEffect(() => {
    void load()
  }, [])

  if (auth === 'checking') {
    return (
      <main className="page">
        <p className="muted">Loading…</p>
      </main>
    )
  }

  if (auth === 'out') {
    return <Login />
  }

  return (
    <WeatherApp
      state={state}
      onRefresh={() => void load()}
      onLogout={async () => {
        await logout()
        setAuth('out')
      }}
    />
  )
}

function WeatherApp({
  state,
  onRefresh,
  onLogout,
}: {
  state: State
  onRefresh: () => void
  onLogout: () => void
}) {
  // Which screen is showing. Local state rather than a route: the app has no router,
  // and adding one for two panels would be a dependency for its own sake. The cost is
  // that the tab is not in the URL, so it cannot be linked to or survive a reload.
  const [tab, setTab] = useState<Tab>('forecast')
  const [unit, setUnit] = useState<Unit>('C')

  return (
    <main className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">This is my 7-Day Weather</h1>
          <p className="page__subtitle">
            Random Forecast served from the ASP.NET Core API
          </p>
        </div>

        <div className="controls">
          {/* Unit and refresh act on the forecast, so they only belong to that tab. */}
          {tab === 'forecast' && (
            <>
              <div className="unit-toggle" role="group" aria-label="Temperature unit">
                <button
                  className={unit === 'C' ? 'unit-toggle__btn is-active' : 'unit-toggle__btn'}
                  onClick={() => setUnit('C')}
                  aria-pressed={unit === 'C'}
                >
                  °C
                </button>
                <button
                  className={unit === 'F' ? 'unit-toggle__btn is-active' : 'unit-toggle__btn'}
                  onClick={() => setUnit('F')}
                  aria-pressed={unit === 'F'}
                >
                  °F
                </button>
              </div>

              <button
                className="refresh"
                onClick={onRefresh}
                disabled={state.status === 'loading'}
              >
                {state.status === 'loading' ? 'Loading…' : 'Refresh'}
              </button>
            </>
          )}

          <button className="logout" onClick={onLogout}>
            Log out
          </button>
        </div>
      </header>

      <nav className="tabs">
        <button
          type="button"
          className={tab === 'forecast' ? 'tabs__btn is-active' : 'tabs__btn'}
          onClick={() => setTab('forecast')}
          aria-current={tab === 'forecast'}
        >
          Forecast
        </button>
        <button
          type="button"
          className={tab === 'locations' ? 'tabs__btn is-active' : 'tabs__btn'}
          onClick={() => setTab('locations')}
          aria-current={tab === 'locations'}
        >
          Locations
        </button>
        <button
          type="button"
          className={tab === 'contacts' ? 'tabs__btn is-active' : 'tabs__btn'}
          onClick={() => setTab('contacts')}
          aria-current={tab === 'contacts'}
        >
          Contacts
        </button>
      </nav>

      {tab === 'locations' && <LocationManager />}

      {/* Unrelated to weather on purpose: this panel demonstrates Ebi.Common.Bol
          reshaping data fetched from an external service. */}
      {tab === 'contacts' && <ContactPanel />}

      {tab === 'forecast' && state.status === 'loading' && (
        <p className="muted">Fetching the forecast…</p>
      )}
      {tab === 'forecast' && state.status === 'error' && (
        <p className="error" role="alert">
          Couldn’t load the forecast: {state.message}
        </p>
      )}
      {tab === 'forecast' && state.status === 'ready' && (
        <WeatherList items={state.data} unit={unit} />
      )}
    </main>
  )
}
